import React, { useState } from 'react';
import { FileText, FileSpreadsheet, FileType, File as FileIcon, Download, ZoomIn, X } from 'lucide-react';

/**
 * Shared attachment thumbnail renderer.
 *
 * Accepts either:
 *   - a data URL string (`data:image/png;base64,...`, `data:application/pdf;base64,...`)
 *   - a remote URL string ending in a known extension (.pdf, .docx, ...)
 *
 * Renders:
 *   - Image → thumbnail clickable to open full-screen lightbox
 *   - PDF → red FileText icon + "PDF" label + click-to-open new tab
 *   - Excel/CSV → green spreadsheet icon
 *   - Word → blue document icon
 *   - Other → generic file icon
 */
function detectKind(url) {
  if (typeof url !== 'string') return 'other';
  const lower = url.toLowerCase();
  if (lower.startsWith('data:image/')) return 'image';
  if (lower.startsWith('data:application/pdf')) return 'pdf';
  if (lower.startsWith('data:application/vnd.openxmlformats-officedocument.spreadsheetml')) return 'excel';
  if (lower.startsWith('data:application/vnd.ms-excel')) return 'excel';
  if (lower.startsWith('data:application/vnd.openxmlformats-officedocument.wordprocessingml')) return 'word';
  if (lower.startsWith('data:application/msword')) return 'word';
  if (lower.startsWith('data:')) return 'other';
  // Plain URL — detect by extension
  if (/\.(png|jpe?g|gif|webp|heic|heif|bmp|svg)(\?|#|$)/i.test(lower)) return 'image';
  if (/\.pdf(\?|#|$)/i.test(lower)) return 'pdf';
  if (/\.(xlsx?|csv)(\?|#|$)/i.test(lower)) return 'excel';
  if (/\.(docx?)(\?|#|$)/i.test(lower)) return 'word';
  return 'other';
}

function fileLabel(kind) {
  switch (kind) {
    case 'image': return 'Image';
    case 'pdf': return 'PDF';
    case 'excel': return 'Excel';
    case 'word': return 'Word Doc';
    default: return 'File';
  }
}

export default function AttachmentGrid({
  attachments = [],
  emptyMessage = 'No attachments available.',
  columns = 3,
}) {
  const [lightbox, setLightbox] = useState(null);

  if (!Array.isArray(attachments) || attachments.length === 0) {
    return (
      <p className="text-center text-gray-500 py-6 text-sm">{emptyMessage}</p>
    );
  }

  const gridCols = columns === 2 ? 'grid-cols-2' : columns === 4 ? 'grid-cols-4' : 'grid-cols-2 md:grid-cols-3';

  return (
    <>
      <div className={`grid ${gridCols} gap-3`}>
        {attachments.map((att, idx) => {
          const url = typeof att === 'string' ? att : (att?.url || att?.dataUrl || '');
          const name = (typeof att === 'object' ? att?.name : '') || `Attachment ${idx + 1}`;
          const kind = detectKind(url);
          if (kind === 'image') {
            return (
              <div key={idx} className="relative group">
                <img
                  src={url}
                  alt={name}
                  className="w-full h-40 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition"
                  onClick={() => setLightbox(url)}
                  data-testid={`attachment-image-${idx}`}
                />
                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                  {idx + 1}/{attachments.length}
                </span>
                <button
                  onClick={() => setLightbox(url)}
                  className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                  title="Zoom"
                >
                  <ZoomIn size={14} />
                </button>
              </div>
            );
          }
          // Non-image — file card
          const iconMap = {
            pdf: <FileText className="text-rose-600" size={36} />,
            excel: <FileSpreadsheet className="text-emerald-600" size={36} />,
            word: <FileType className="text-blue-600" size={36} />,
            other: <FileIcon className="text-gray-500" size={36} />,
          };
          return (
            <a
              key={idx}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              download={name}
              className="flex flex-col items-center justify-center h-40 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition p-3"
              data-testid={`attachment-file-${idx}`}
              title={`Open ${fileLabel(kind)}`}
            >
              {iconMap[kind] || iconMap.other}
              <span className="mt-2 text-xs font-medium text-gray-700">{fileLabel(kind)}</span>
              <span className="text-[10px] text-gray-500 truncate w-full text-center mt-0.5">{name}</span>
              <span className="text-[10px] text-blue-600 mt-1 flex items-center gap-0.5">
                <Download size={10} /> Click to open
              </span>
            </a>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="attachment-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 bg-white/20 text-white p-2 rounded-full hover:bg-white/30"
          >
            <X size={20} />
          </button>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </>
  );
}
